import React from 'react';
import ScopeCard from './ScopeCard';
import UsdcPaymentPrompt from './UsdcPaymentPrompt';
import CheckoutPayCard from './CheckoutPayCard';
import PaymentMethodPicker from './PaymentMethodPicker';
import HostingPaymentCard from './HostingPaymentCard';
import DomainPaymentCard from './DomainPaymentCard';
import ConfirmationCard from './ConfirmationCard';
import PaymentReceipt from './PaymentReceipt';
import '../styles/MessageBubble.css';
import '../styles/PaymentCards.css';

/**
 * Sanitize URLs to prevent javascript: protocol XSS attacks.
 * Only allows http:, https:, and mailto: protocols.
 */
function sanitizeUrl(url) {
  if (!url) return '#';
  
  try {
    const parsed = new URL(url, window.location.origin);
    if (['http:', 'https:', 'mailto:'].includes(parsed.protocol)) {
      return url;
    }
    return '#'; // Unsafe protocol - neutralize
  } catch {
    // Relative URLs or malformed - allow if no colon before first slash
    if (url.includes(':') && !url.startsWith('/')) {
      return '#'; // Has protocol-like prefix, block it
    }
    return url;
  }
}

/**
 * Parse markdown-style formatting into React elements.
 * Supports: [text](url) links and **bold** text
 */
function parseMarkdown(text) {
  if (!text) return text;

  // Combined regex for links and bold
  // Matches: [link text](url) or **bold text**
  const pattern = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;

  const parts = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = pattern.exec(text)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1] && match[2]) {
      // Link: [text](url) - sanitize URL to prevent javascript: XSS
      parts.push(
        <a
          key={`link-${keyIndex++}`}
          href={sanitizeUrl(match[2])}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-link"
        >
          {match[1]}
        </a>
      );
    } else if (match[3]) {
      // Bold: **text** - recursively parse inner content for nested links
      parts.push(
        <strong key={`bold-${keyIndex++}`}>
          {parseMarkdown(match[3])}
        </strong>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

function MessageBubble({ 
  message, 
  onScopeCardAction, 
  onDomainSelect, 
  disabled,
  conversationId,
  isPaymentConfirmed = false,
  onPaymentConfirmed
}) {
  const { role, content, message_type, metadata, created_at } = message;

  // Hidden messages (e.g., USDC Card 2 after successful payment) — skip render entirely
  if (metadata?.hidden === true) {
    return null;
  }

  const isCustomer = role === 'customer';

  const formatTime = (dateString) => {
    if (!dateString) return '';
    // Ensure UTC interpretation: append 'Z' if no timezone indicator present
    // Server may send timestamps without timezone suffix (e.g., "2025-06-26T14:30:00")
    let isoString = dateString;
    if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}(:\d{2})?$/)) {
      isoString = isoString + 'Z';
    }
    return new Date(isoString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Render special message types
  const renderContent = () => {
    switch (message_type) {
      case 'scope_card':
        return (
          <div className="message-special">
            <p className="message-text">{parseMarkdown(content)}</p>
            <ScopeCard
              metadata={metadata}
              onApprove={() => onScopeCardAction('approve', message.id)}
              onDecline={() => onScopeCardAction('decline', message.id)}
              disabled={disabled}
            />
          </div>
        );

      case 'hosting_payment':
        if (metadata?.confirmation_step) {
          return <ConfirmationCard metadata={metadata} conversationId={conversationId} />;
        }
        if (metadata?.checkout_url && !metadata?.has_card && !metadata?.has_wallet) {
          return <div className="message-special"><p className="picker-status">✅ Paid</p></div>;
        }
        return (
          <div className="message-special">
            {content && <p className="message-text">{parseMarkdown(content)}</p>}
            <HostingPaymentCard metadata={metadata} conversationId={conversationId} messageId={message.id} />
          </div>
        );

      case 'domain_payment':
        if (metadata?.confirmation_step) {
          return <ConfirmationCard metadata={metadata} conversationId={conversationId} />;
        }
        return (
          <div className="message-special">
            {content && <p className="message-text">{parseMarkdown(content)}</p>}
            <DomainPaymentCard metadata={metadata} conversationId={conversationId} messageId={message.id} />
          </div>
        );

      case 'payment_picker':
        // Dual-rail payment method picker for preference='both' customers
        return (
          <div className="message-special">
            <PaymentMethodPicker 
              message={message} 
              conversationId={conversationId}
            />
          </div>
        );

      case 'payment_prompt':
        // Link 20 D4: the USDC checkout card (Pay button, any browser wallet) — also the "still owing" message
        if (metadata?.payment_type === 'usdc_checkout') {
          return (
            <div className="message-special">
              {content && <p className="message-text">{parseMarkdown(content)}</p>}
              <CheckoutPayCard metadata={metadata} messageId={message.id} onPaymentConfirmed={onPaymentConfirmed} />
            </div>
          );
        }
        // The old USDC card (send exactly X to the treasury from a linked wallet) — kept for cards already posted
        if (metadata?.payment_type === 'usdc') {
          return (
            <div className="message-special">
              <UsdcPaymentPrompt 
                metadata={metadata}
                confirmed={isPaymentConfirmed}
                onPaymentConfirmed={onPaymentConfirmed}
              />
            </div>
          );
        }
        // Existing Stripe payment prompt
        return (
          <div className="message-special">
            <p className="message-text">{parseMarkdown(content)}</p>
            {metadata?.checkout_url && (
              <a
                href={metadata.checkout_url}
                className="btn btn-primary payment-btn"
              >
                Add Payment Method
              </a>
            )}
          </div>
        );

      case 'payment_receipt':
        return (
          <div className="message-special">
            <PaymentReceipt metadata={metadata} createdAt={created_at} />
          </div>
        );

      case 'delivery_link':
        return (
          <div className="message-special">
            <p className="message-text">{parseMarkdown(content)}</p>
            {metadata?.preview_url && (
              <a
                href={metadata.preview_url}
                className="btn btn-primary delivery-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                🎉 View Your Project
              </a>
            )}
          </div>
        );

      case 'domain_options':
        return (
          <div className="message-special">
            <p className="message-text">{parseMarkdown(content)}</p>
            {metadata?.suggestions && (
              <div className="domain-options">
                {metadata.suggestions.map((item, index) => {
                  // Handle both old format (string) and new format (object)
                  const domain = typeof item === 'string' ? item : item.domain;
                  const price = typeof item === 'string' ? metadata.price_cents : item.price_cents;
                  return (
                    <button
                      key={index}
                      className="domain-option domain-option-clickable"
                      onClick={() => onDomainSelect && onDomainSelect(domain)}
                      disabled={disabled}
                    >
                      <span className="domain-name">{domain}</span>
                      {price && (
                        <span className="domain-price">
                          ${(price / 100).toFixed(0)}/yr
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );

      default:
        return <p className="message-text">{parseMarkdown(content)}</p>;
    }
  };

  return (
    <div className={`message-bubble ${isCustomer ? 'customer' : 'gatekeeper'}`}>
      <div className="message-content">{renderContent()}</div>
      <span className="message-time">{formatTime(created_at)}</span>
    </div>
  );
}

export default MessageBubble;
